<ng-container *ngIf="preview; else skeleton__card">
	<!-- card-icon -->
	<div class="title-icon" [matTooltip]="titleIconTooltip">
		<!-- when result is loading -->
		<cls-spinner *ngIf="loading" title="Spinner" width="20px"></cls-spinner>

		<!-- when result is disabled (locked) -->
		<mat-icon class="success-icon" *ngIf="disabled" style="opacity: 0.5">lock</mat-icon>

		<!-- when result has error -->
		<mat-icon class="error-icon" *ngIf="!loading && result === null" color="warn">error</mat-icon>

		<!-- when result is loaded successfully and it is internal result-->
		<mat-icon
			class="success-icon"
			*ngIf="!loading && !disabled && result !== null && !preview.url"
			style="opacity: 0.5">
			<ng-container [ngSwitch]="preview.type">
				<ng-container *ngSwitchCase="eResultPreviewType.Batch">dns</ng-container>
				<ng-container *ngSwitchCase="eResultPreviewType.Database">dns</ng-container>
				<ng-container *ngSwitchDefault>storage</ng-container>
			</ng-container>
		</mat-icon>

		<!-- when result is loaded successfully and it is external result-->
		<img
			alt="Result image"
			*ngIf="!loading && result !== null && preview.url"
			[src]="'https://www.google.com/s2/favicons?domain=' + urlDomain" />
	</div>

	<!-- customer body design -->
	<div *ngIf="preview?.component" class="custom-component-wrapper">
		<ng-container #vcr></ng-container>
	</div>

	<!-- default body design -->
	<ng-container *ngIf="!preview?.component">
		<div header class="title" [class]="'title-' + directionService.dir">
			<button
				[disabled]="disabled"
				class="result-title-btn"
				mat-button
				(click)="onTitleClick()"
				aria-label="Click to see report against this result.">
				<span
					class="result-title"
					[ngClass]="{ 'ellipsis-flex-text': preview.type === eResultPreviewType.Internet }"
					[matTooltip]="preview.title"
					[matTooltipDisabled]="preview.type !== eResultPreviewType.Internet">
					{{ preview.title }}
				</span>
			</button>
			<button
				class="menu-toggle-button"
				title="Toggle Menu"
				aria-label="Opens up options menu for this result."
				aria-haspopup="menu"
				mat-icon-button
				[matMenuTriggerFor]="menu"
				matTooltip="Toggle Menu">
				<mat-icon svgIcon="arrow_drop_down"></mat-icon>
			</button>
			<mat-menu #menu="matMenu" class="result-card-actions">
				<button [title]="translations?.RESULT_CARD?.ACTIONS?.EXCLUDE || 'Exclude'" mat-menu-item (click)="hideResult()">
					{{ translations?.RESULT_CARD?.ACTIONS?.EXCLUDE || 'Exclude' }}
				</button>
				<button
					title="action.name"
					aria-label="Click to choose option."
					*ngFor="let action of resultCardActions"
					mat-menu-item
					(click)="action.action(preview)">
					{{ action.name }}
				</button>
			</mat-menu>
		</div>

		<div
			body
			class="meta-data"
			[title]="preview.metadata?.filename"
			*ngIf="preview.type != eResultPreviewType.Internet && preview.metadata?.filename">
			<span>{{ preview.metadata?.filename }}</span>
		</div>

		<a class="link-styles" *ngIf="preview.url" [href]="preview.url" [title]="preview.url" target="_blank">
			{{ preview.url }}
		</a>

		<p
			body
			*ngIf="preview.metadata?.author || preview.metadata?.organization || previewDate"
			class="meta-data"
			#metaDataRef
			[matTooltip]="metaDataToolTip">
			<span>{{ preview.metadata?.author }}</span>
			<span *ngIf="preview.metadata?.author && preview.metadata?.organization" class="dot-divider">&nbsp;•&nbsp;</span>
			<span *ngIf="preview.metadata?.organization">{{ preview.metadata?.organization }}</span>
			<span *ngIf="(preview.metadata?.author || preview.metadata?.organization) && previewDate" class="dot-divider">
				&nbsp;•&nbsp;
			</span>
			<span *ngIf="previewDate">{{ previewDate }}</span>
		</p>
		<p
			body
			tabindex="0"
			role="button"
			(keyup.enter)="onTitleClick()"
			(click)="onTitleClick()"
			*ngIf="preview.introduction"
			[style.cursor]="disabled ? 'default' : 'pointer'"
			aria-label="Click to see report against this result.">
			{{ preview.introduction }}
		</p>
	</ng-container>

	<mat-divider></mat-divider>

	<div footer fxLayout="row" fxLayoutAlign="space-between center">
		<div [ngClass]="{ 'text-identical': true }" *ngIf="source" style="color: black; font-weight: 400">
			{{ (similarWords$ | async) / (source?.metadata.words - source?.metadata.excluded) | similarity }}
			{{ translations?.RESULT_CARD?.SIMILAR_WORDS || 'similar words' }}
		</div>
		<span></span>
		<div
			[dir]="directionService.dir"
			fxLayout="row"
			fxLayoutGap="1em"
			style="height: 28px"
			[satPopoverAnchor]="popover"
			satPopoverHover
			[tabIndex]="0"
			aria-label="Use the enter key to see result similarity types."
			aria-haspopup="true"
			role="button"
			(keyup.enter)="popover.toggle()">
			<span class="dot identical"></span>
			<span class="dot minor-changes"></span>
			<span class="dot related"></span>
		</div>
	</div>

	<sat-popover #popover horizontalAlign="before" [restoreFocus]="true" [autoFocus]="true">
		<div fxLayout="column nowrap" fxLayoutAlign="start start" class="sat-tooltip mat-typography" [tabIndex]="0">
			<!--  -->
			<ng-container *ngIf="!source">
				<div>
					<cls-spinner title="Spinner" width="30px"></cls-spinner>
				</div>
			</ng-container>
			<!--  -->
			<ng-container *ngIf="source">
				<div class="text-identical">
					{{ translations?.SHARED?.MATCH_TYPES?.IDENTICAL || 'Identical' }} -
					{{ result?.statistics.identical / (source?.metadata.words - source?.metadata.excluded) | similarity }}
					{{ translations?.SHARED?.WORDS || 'words' }}.
				</div>
				<div class="text-minor-changes">
					{{ translations?.SHARED?.MATCH_TYPES?.MINOR_CHANGES || 'Minor Changes' }} -
					{{ result?.statistics.minorChanges / (source?.metadata.words - source?.metadata.excluded) | similarity }}
					{{ translations?.SHARED?.WORDS || 'words' }}.
				</div>
				<div class="text-related">
					{{ translations?.SHARED?.MATCH_TYPES?.RELATED_MEANING || 'Paraphrased' }} -
					{{ result?.statistics.relatedMeaning / (source?.metadata.words - source?.metadata.excluded) | similarity }}
					{{ translations?.SHARED?.WORDS || 'words' }}.
				</div>
			</ng-container>
			<!--  -->
		</div>
	</sat-popover>
</ng-container>

<ng-template #skeleton__card>
	<div class="result-loading">
		<p>
			<ngx-skeleton-loader></ngx-skeleton-loader>
		</p>
		<ngx-skeleton-loader></ngx-skeleton-loader>
		<ngx-skeleton-loader></ngx-skeleton-loader>
		<ngx-skeleton-loader></ngx-skeleton-loader>
		<p>
			<ngx-skeleton-loader></ngx-skeleton-loader>
		</p>
	</div>
	<mat-divider></mat-divider>
	<div footer fxLayout="row" fxLayoutAlign="space-between stretch">
		<ngx-skeleton-loader fxFlex="30" [theme]="{ height: '28px', margin: 0 }"></ngx-skeleton-loader>
		<div fxLayout="row" fxLayoutGap="1em">
			<ngx-skeleton-loader
				[theme]="{ height: '28px', width: '28px', margin: 0 }"
				appearance="circle"></ngx-skeleton-loader>
			<ngx-skeleton-loader
				[theme]="{ height: '28px', width: '28px', margin: 0 }"
				appearance="circle"></ngx-skeleton-loader>
			<ngx-skeleton-loader
				[theme]="{ height: '28px', width: '28px', margin: 0 }"
				appearance="circle"></ngx-skeleton-loader>
		</div>
	</div>
</ng-template>
